import { Button } from "primereact/button";
import { InputText } from "primereact/inputtext";
import { OverlayPanel } from "primereact/overlaypanel";
import { SelectButton } from "primereact/selectbutton";
import { forwardRef, useEffect, useImperativeHandle, useRef, useState, useCallback } from "react";
import { TIME, DATETIME } from "../../../constants/time";
import { convertHHMMFormatToTime, convertTimeToHHMMFormat } from "../../../helpers/datetimeHelpers";
import useTranslation from "../../../hook/useTranslation";
import { HEADER_INDEX } from "../../../constants/app-constants";
import DropdownSelectTime from "./DropdownSelectTime";

const DropdownSelectDayTime = forwardRef(({ commonProps, onPickTime, onPickDay, dayOptions, labelKey, valueKey, selectedTimeState, selectedDayState }, ref) => {
  const checkIsValidDate = (dateInput) => {
    const date = dateInput instanceof Date ? dateInput : new Date(dateInput);
    return date instanceof Date && !isNaN(date.getTime());
  };
  const allDayTransValue = useTranslation("COMMON_ITEMS.WEEKDAY.ALL");
  const allDaysOption = [{"key": "ALL", "value": allDayTransValue}];

  const [startTimeDateState, setStartTimeDateState] = useState(null);
  const [endTimeDateState, setEndTimeDateState] = useState(null);
  const openDropdown = useRef(false);
  const [showSelectedLabel, setShowSelectedLabel] = useState(false);
  const [focusedCalendar, setFocusedCalendar] = useState(null);
  const inputRef = useRef(null);
  const defaultStartTime = new Date();
  defaultStartTime.setHours(0, 0, 0, 0);

  const defaultEndTime = new Date(defaultStartTime);
  defaultEndTime.setHours(23, commonProps?.stepMinute, 0, 0);

  useImperativeHandle(ref, () => ({
    handleClear,
  }));

  const onDayChange = (e) => {
    const value = e.value;
    onPickDay(value);
  };

  const onAllDaysClick = () => {
    const value = dayOptions.map((d) => d[valueKey]);
    onPickDay(value);
  };

  const roundToValidMinute = (dateTime, stepMinute) => {
    const currentMinutes = dateTime.getMinutes();
    const step = stepMinute || 30;
    var timeDefault = new Date(DATETIME.MIN);
    var minuteDefault = timeDefault.getMinutes();
    let validMinute;
    if (minuteDefault !== currentMinutes && step !== currentMinutes) {
      let timeAdd = new Date(dateTime);
      let timeSub = new Date(dateTime);
      timeAdd = new Date(timeAdd.setMinutes(dateTime.getMinutes() + step));
      timeSub = new Date(timeSub.setMinutes(dateTime.getMinutes() - step));
      if (timeAdd.getMinutes() === minuteDefault) {
        validMinute = step;
      }

      if (timeSub.getMinutes() === step) {
        validMinute = minuteDefault
      }
    } else {
      validMinute = currentMinutes;
    }
    
    dateTime.setMinutes(validMinute, 0, 0);
    return dateTime;
  };

  const onStartTimeChange = (e) => {
    const dateObject = e;
    if (!checkIsValidDate(dateObject)) {
      setStartTimeDateState(null);
      return;
    }
    
    let startTimeDate = new Date(dateObject);
    
    startTimeDate = roundToValidMinute(startTimeDate, commonProps?.stepMinute);

    const minTime = new Date(startTimeDate);
    minTime.setHours(0, 0, 0, 0);

    const maxTime = new Date(startTimeDate);
    maxTime.setHours(23, 0, 0, 0);

    let adjustedTime;
    if (startTimeDate > maxTime) {
      adjustedTime = maxTime;
    } else if (startTimeDate < minTime) {
      adjustedTime = minTime;
    } else {
      adjustedTime = startTimeDate;
    }
    if (endTimeDateState && checkIsValidDate(endTimeDateState) && endTimeDateState <= adjustedTime) {
      const newEndTimeAdjusted = new Date(adjustedTime);
      newEndTimeAdjusted.setMinutes(newEndTimeAdjusted.getMinutes() + commonProps?.stepMinute);
      if (newEndTimeAdjusted.getMinutes() >= 60) {
        newEndTimeAdjusted.setHours(newEndTimeAdjusted.getHours() + 1);
        newEndTimeAdjusted.setMinutes(0, 0, 0);
      }
      setEndTimeDateState(roundToValidMinute(newEndTimeAdjusted, commonProps?.stepMinute));
    }
    
    setStartTimeDateState(new Date(adjustedTime.getTime()));

    if (endTimeDateState === null) {
      setEndTimeDateState(defaultEndTime);
    }
  };

  const onEndTimeChange = (e) => {
    const dateObject = e;
    if (!checkIsValidDate(dateObject)) {
      setEndTimeDateState(null);
      return;
    }

    let endTimeDate = new Date(dateObject);
    
    endTimeDate = roundToValidMinute(endTimeDate, commonProps?.stepMinute);

    const minTime = new Date(endTimeDate);
    minTime.setHours(0, commonProps?.stepMinute, 0, 0);

    const maxTime = new Date(endTimeDate);
    maxTime.setHours(23, commonProps?.stepMinute, 0, 0);

    if (endTimeDate <= minTime) {
      endTimeDate = minTime;
    } else if (endTimeDate > maxTime) {
      endTimeDate = maxTime;
    }

    if (startTimeDateState && checkIsValidDate(startTimeDateState)) {
      const requiredStartTime = new Date(endTimeDate);
      requiredStartTime.setMinutes(requiredStartTime.getMinutes() - commonProps?.stepMinute);
      if (requiredStartTime.getMinutes() < 0) {
        requiredStartTime.setHours(requiredStartTime.getHours() - 1);
        requiredStartTime.setMinutes(60 - commonProps?.stepMinute, 0, 0);
      }

      if (startTimeDateState >= endTimeDate) {
        setStartTimeDateState(roundToValidMinute(requiredStartTime, commonProps?.stepMinute));
      }
    }
    setEndTimeDateState(new Date(endTimeDate.getTime()));

    if (startTimeDateState === null) {
      setStartTimeDateState(defaultStartTime);
    }
  };

  const handleCloseTimeChooseModal = () => {
    if (startTimeDateState === null || endTimeDateState === null) {
      return;
    }
    onPickTime({ startTime: convertTimeToHHMMFormat(startTimeDateState), endTime: convertTimeToHHMMFormat(endTimeDateState) });
  };

  const handleInputClick = (e) => {
    openDropdown.current.toggle(e);
  };

  const handleClear = () => {
    setShowSelectedLabel(false);
    setStartTimeDateState(null);
    setEndTimeDateState(null);
    onPickTime(null);
    onPickDay(null);
  };

  const handleFocus = (panelId) => {
    setFocusedCalendar(panelId);
  };

  const handleBlur = () => {
    setFocusedCalendar(null);
  };

  useEffect(() => {
    if (selectedTimeState?.startTime != null || selectedDayState?.length) {
      setShowSelectedLabel(true);
    } else {
      setShowSelectedLabel(false);
    }

    if (selectedTimeState?.startTime) {
      setStartTimeDateState(convertHHMMFormatToTime(selectedTimeState?.startTime));
    } else {
      setStartTimeDateState(null);
    }

    if (selectedTimeState?.endTime) {
      setEndTimeDateState(convertHHMMFormatToTime(selectedTimeState?.endTime));
    } else {
      setEndTimeDateState(null);
    }
  }, [selectedTimeState, selectedDayState]);

  const handleOverlayShow = () => {
    if (openDropdown.current) {
      const overlayElement = openDropdown.current.getElement();
      if (overlayElement) {
        const sidebar = document.querySelector(".p-sidebar-left");
        if (sidebar) {
          overlayElement.style.zIndex = `${HEADER_INDEX + 2}`;
        } else {
          overlayElement.style.zIndex = `${HEADER_INDEX - 1}`;
        }
    }
    }
  };

  const handleOverlayHide = () => {
    if (openDropdown.current) {
      const overlayElement = openDropdown.current.getElement();
      if (overlayElement) {
        overlayElement.style.zIndex = '';
      }
    }
  };

  return (
    <div className="relative">
      <div ref={inputRef} className="relative w-full flex dropdown-picker-time custom-dropdown-container">
        <InputText
          className="keduall-typography text-16-semibold-600 keduall-form dropdown-input p-dropdown z-1 keduall-typography text-default-txt text-ellipsis custom-input custom-placeholder"
          type="text"
          value={
            showSelectedLabel
              ? `${
                  selectedTimeState?.startTime != null && selectedTimeState?.endTime != null
                    ? `${selectedTimeState.startTime}-${selectedTimeState.endTime}${selectedDayState?.length > 0 ? " - " : ""}`
                    : ""
                }${selectedDayState?.length > 0 ? selectedDayState?.map((option) => dayOptions?.find((d) => d[valueKey] === option)[labelKey]).join(", ") : ""}`
              : ""
          }
          onClick={(e) => handleInputClick(e)}
          readOnly
          placeholder={`${TIME.MIN} - ${TIME.MIN}`}
        />
        {showSelectedLabel && <Button icon="pi pi-times z-5 flex" className="p-button-text absolute keduall-form dropdown-input icon icon-times-circle" onClick={handleClear} />}
        <i className="pi cursor-pointer pi-angle-down keduall-form dropdown-input icon-angle-down-small absolute z-4 flex" onClick={handleInputClick}></i>
      </div>
      <OverlayPanel onShow={handleOverlayShow} onHide={handleOverlayHide} ref={openDropdown} appendTo={inputRef.current} className="left-0 top-100 w-webkit-fill-available mt-2">
        <div className="p-dropdown-panel keduall-form dropdown-input w-webkit-fill-available p-dropdown flex flex-column gap-075 cursor-auto">
          <div className="flex flex-row justify-content-between align-items-center w-full">
            <div className="w-full flex keduall-typography text-16-bold-700 text-default-txt mt-2 mb-1">{commonProps?.dayPickerTitle}</div>
            <Button
              className="w-full keduall-buttons button-outlined-default-small-primary keduall-typography text-14-semibold-600 text-default flex gap-1 flex-initial"
              onClick={() => onAllDaysClick()}
              options={allDaysOption}
            >{allDayTransValue}</Button>  
          </div>
          <div className="w-full flex flex-row justify-content-center align-items-center gap-1 dropdown-picker-time">
            <SelectButton
              className="w-full keduall-buttons button-outlined-default-small-primary-button-group keduall-typography text-14-semibold-600 text-default flex-wrap flex gap-1"
              value={selectedDayState}
              onChange={onDayChange}
              optionValue={valueKey}
              optionLabel={labelKey}
              options={dayOptions}
              multiple
            />
          </div>
          {/* Time picker */}
          <div className="w-full flex keduall-typography text-16-bold-700 text-default-txt mt-2 mb-1">{commonProps?.timePickerTitle}</div>
          <div className="flex flex-row justify-content-center align-items-center gap-075 w-full mb-2">
            <div className="flex flex-column w-full">
              <DropdownSelectTime
                hideOnEscape={false}
                readOnlyInput={true}
                onHide={handleCloseTimeChooseModal}
                panelId="start-time-calender"
                id="start-time"
                className="w-full"
                value={startTimeDateState}
                onChange={(e) => onStartTimeChange(e)}
                placeholder={TIME.MIN}
                commonProps={commonProps}
                stepMinute={commonProps?.stepMinute}
              />
            </div>
            <h5 className="flex keduall-typography heading-h5-normal-400">&#126;</h5>
            <div className="flex flex-column w-full">
              <DropdownSelectTime
                hideOnEscape={false}
                readOnlyInput={true}
                onHide={handleCloseTimeChooseModal}
                panelId="end-time-calender"
                id="end-time"
                className="w-full"
                value={endTimeDateState}
                onChange={(e) => onEndTimeChange(e)}
                placeholder={TIME.MAX}
                commonProps={commonProps}
                stepMinute={commonProps?.stepMinute}
              />
            </div>
          </div>
        </div>
      </OverlayPanel>
    </div>
  );
});

export default DropdownSelectDayTime;
